const SVG_DATA_URI =
  /^data:image\/svg\+xml(?:;charset=[^;,]+)?(?:;(base64))?,(.*)$/i;

function decodeSvgDataUri(uri) {
  const match = uri.match(SVG_DATA_URI);

  if (!match) return null;

  try {
    if (!match[1] && match[2].includes('%')) {
      return decodeURIComponent(match[2]);
    }

    const binary = atob(match[2]);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder().decode(bytes);
    }

    return binary;
  } catch {
    return null;
  }
}

function getStyleValue(style, key) {
  if (!style) return null;

  const match = style.match(new RegExp(`(?:^|;)\\s*${key}=`));

  if (!match) return null;

  const start = match.index + match[0].length;
  const comma = style.indexOf(',', start);
  const searchFrom =
    style.slice(start, start + 5).toLowerCase() === 'data:' && comma !== -1
      ? comma
      : start;
  const end = style.indexOf(';', searchFrom);

  return style.slice(start, end === -1 ? undefined : end);
}

function findElementById(root, id) {
  if (!root || !id) return null;

  return Array.from(root.querySelectorAll('[id]')).find(
    (node) => node.getAttribute('id') === id
  );
}

function getElementPath(root, node) {
  const path = [];
  let current = node;

  while (current && current !== root) {
    const parent = current.parentElement;

    if (!parent) return null;

    path.unshift(Array.from(parent.children).indexOf(current));
    current = parent;
  }

  return current === root ? path : null;
}

function getElementAtPath(root, path) {
  return path?.reduce((node, index) => node?.children?.[index] || null, root);
}

function getSourceSvg(model, cellId, name) {
  const cells = Array.from(model.querySelectorAll('[id]')).filter(
    (node) => node.getAttribute('id') === cellId
  );

  const sourceCell =
    cells.find((node) => !name || node.getAttribute('name') === name) ||
    cells[0];

  if (!sourceCell) return null;

  const mxCell =
    sourceCell.localName === 'mxCell'
      ? sourceCell
      : Array.from(sourceCell.children).find(
          (node) => node.localName === 'mxCell'
        );
  const imageUri = getStyleValue(mxCell?.getAttribute('style'), 'image');
  const source = imageUri && decodeSvgDataUri(imageUri);

  if (!source) return null;

  const sourceDocument = new DOMParser().parseFromString(
    source,
    'image/svg+xml'
  );

  return sourceDocument.querySelector('parsererror')
    ? null
    : sourceDocument.documentElement;
}

function findRenderedNode(sourceSvg, sourceNode, renderedSvg) {
  const id = sourceNode.getAttribute('id');
  const byId = id && findElementById(renderedSvg, id);

  if (byId) return byId;

  return getElementAtPath(renderedSvg, getElementPath(sourceSvg, sourceNode));
}

/**
 * Restores metadata removed when recent diagrams.net versions inline and
 * sanitize SVG image cells. The exported cell ID is used to locate the exact
 * source image in the embedded draw.io model; only TEO metadata is copied.
 */
export function restoreDrawioSvgMetadata(svg) {
  const drawioContent = svg?.getAttribute?.('content');

  if (!drawioContent) return 0;

  const model = new DOMParser().parseFromString(drawioContent, 'text/xml');

  if (model.querySelector('parsererror')) return 0;

  let restored = 0;

  svg.querySelectorAll('[data-name]').forEach((component) => {
    const wrapperId = component.getAttribute('id') || '';

    if (!wrapperId.startsWith('cell-')) return;

    const sourceSvg = getSourceSvg(
      model,
      wrapperId.slice('cell-'.length),
      component.getAttribute('data-name')
    );

    if (!sourceSvg) return;

    const sourceType = sourceSvg.getAttribute('data-type');
    const renderedSvg =
      findElementById(component, sourceSvg.getAttribute('id')) ||
      component.querySelector('svg');

    if (sourceType && !component.hasAttribute('data-type')) {
      component.setAttribute('data-type', sourceType);
      restored += 1;
    }

    if (!renderedSvg) return;

    sourceSvg.querySelectorAll('[data-state]').forEach((sourceState) => {
      const renderedState = findRenderedNode(
        sourceSvg,
        sourceState,
        renderedSvg
      );

      if (renderedState && !renderedState.hasAttribute('data-state')) {
        renderedState.setAttribute(
          'data-state',
          sourceState.getAttribute('data-state')
        );
        restored += 1;
      }
    });
  });

  return restored;
}

export function restoreDrawioSvgMetadataInText(data) {
  const document = new DOMParser().parseFromString(data, 'image/svg+xml');

  if (document.querySelector('parsererror')) return data;

  restoreDrawioSvgMetadata(document.documentElement);

  return new XMLSerializer().serializeToString(document.documentElement);
}
